import React, { useEffect, useState } from "react";
import Card from "../components/Card";
import CalendarBookings from "../components/CalendarBookings";
import {
  UserIcon,
  CpuChipIcon as GameControllerIcon,
  CurrencyDollarIcon as DollarSignIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { obtenerMetricasDashboard } from "../../api";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import "bootstrap/dist/css/bootstrap.min.css";
import "./custom-tabs.css";
import { Chart } from "chart.js";
const DashboardHome = () => {
  const [metrics, setMetrics] = useState(null);
  const [range, setRange] = useState("day");
  useEffect(() => {
    obtenerMetricasDashboard().then(setMetrics).catch(console.error);
  }, []);

  if (!metrics)
    return (
      <div className="text-center py-4 text-light-gray">Loading metrics...</div>
    );

  const cards = [
    { title: "Total Users", value: metrics.totalUsers, icon: <UserIcon /> },
    {
      title: "Registered Users",
      value: metrics.registeredUsers,
      icon: <CheckCircleIcon />,
    },
    {
      title: "Guest Users",
      value: metrics.unregisteredUsers,
      icon: <XCircleIcon />,
    },
    {
      title: "Games Played",
      value: metrics.totalGamesPlayed,
      icon: <GameControllerIcon />,
    },
    {
      title: "Total Revenue",
      value: `$${Number(metrics.totalRevenue).toLocaleString()}`,
      icon: <DollarSignIcon />,
    },
    {
      title: "Pending Invoices",
      value: metrics.pendingInvoices,
      icon: <ClockIcon />,
    },
    { title: "Admins", value: metrics.totalAdmins, icon: <UserIcon /> },
    {
      title: "Pending Admin Requests",
      value: metrics.pendingAdminRequests,
      icon: <EnvelopeIcon />,
    },
  ];

  const bookingsData = {
    labels: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    datasets: [
      {
        label: "Bookings per Day",
        data: (() => {
          const dayMap = {
            Sunday: 0,
            Monday: 0,
            Tuesday: 0,
            Wednesday: 0,
            Thursday: 0,
            Friday: 0,
            Saturday: 0,
          };
          metrics.bookingsPerDay.forEach(({ day, total }) => {
            const date = new Date(day);
            const weekday = date.toLocaleDateString("en-US", {
              weekday: "long",
            });
            dayMap[weekday] = total;
          });
          return Object.keys(dayMap).map((d) => dayMap[d]);
        })(),
        backgroundColor: "rgb(86,109,135)",
      },
    ],
  };

  const getRevenueData = () => {
    let labels = [];
    let data = [];

    if (range === "day") {
      const entries = metrics.revenuePerDay;
      labels = entries.map((e) =>
        new Date(e.date).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        })
      );
      data = entries.map((e) => e.revenue);
    } else if (range === "week") {
      const today = new Date();
      const day = today.getDay(); // domingo = 0
      const diffToMonday = (day === 0 ? -6 : 1) - day;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diffToMonday);
      monday.setHours(0, 0, 0, 0);

      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return d;
      });

      labels = weekDates.map((d) =>
        d.toLocaleDateString("en-US", { weekday: "short", day: "numeric" })
      );

      const dateStrs = weekDates.map((d) => d.toISOString().slice(0, 10));
      // Mapea fechas en formato 'YYYY-MM-DD'
      const dayRevenueMap = {};
      metrics.revenuePerDay.forEach((entry) => {
        const normalizedDate = new Date(entry.date).toISOString().slice(0, 10);
        dayRevenueMap[normalizedDate] = entry.revenue;
      });

      // Usa fechas normalizadas también para comparación
      data = dateStrs.map((date) => dayRevenueMap[date] ?? 0);
    } else if (range === "month") {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth(); // 0-indexed
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Generar todas las fechas del mes actual
      const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
        const d = new Date(year, month, i + 1);
        return d;
      });

      labels = monthDates.map((d) =>
        d.toLocaleDateString("en-US", { day: "numeric" })
      );

      const dateStrs = monthDates.map((d) => d.toISOString().slice(0, 10));

      // Mapear ingresos del día
      const dayRevenueMap = {};
      metrics.revenuePerDay.forEach((entry) => {
        const normalizedDate = new Date(entry.date).toISOString().slice(0, 10);
        dayRevenueMap[normalizedDate] = entry.revenue;
      });

      data = dateStrs.map((date) => dayRevenueMap[date] ?? 0);
    } else if (range === "year") {
      const currentYear = new Date().getFullYear();
      const months = Array.from({ length: 12 }, (_, i) => i); // 0 to 11

      labels = months.map((monthIndex) =>
        new Date(currentYear, monthIndex, 1).toLocaleDateString("en-US", {
          month: "short",
        })
      );

      const monthMap = {};
      metrics.revenuePerMonth.forEach((entry) => {
        if (entry.month.startsWith(currentYear.toString())) {
          const monthNum = parseInt(entry.month.split("-")[1], 10) - 1; // 0-indexed
          monthMap[monthNum] = entry.revenue;
        }
      });

      data = months.map((i) => monthMap[i] ?? 0);
    }

    return {
      labels,
      datasets: [
        {
          label: `Revenue (${range})`,
          data,
          borderColor: "#38bdf8",
          backgroundColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return null;
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.bottom,
              0,
              chartArea.top
            );
            gradient.addColorStop(0, "rgba(56, 189, 248, 0.1)");
            gradient.addColorStop(1, "rgba(56, 189, 248, 0.6)");
            return gradient;
          },
          fill: true,
          tension: 0.3,
          pointBackgroundColor: "#0ea5e9",
          pointBorderColor: "#0369a1",
        },
      ],
    };
  };

  return (
    <div className="container py-6 text-light-gray">
      <h1 className="text-3xl font-bold text-center text-primary mb-6 font-audiowide">
        Dashboard Overview
      </h1>

      <Tabs
        defaultActiveKey="overview"
        id="dashboard-tabs"
        className="mb-4 custom-tabs"
        justify
      >
        <Tab eventKey="overview" title="Overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
            {cards.map((metric, i) => (
              <div
                key={i}
                className="p-5 rounded-xl border border-primary shadow-custom transition-transform hover:scale-[1.03] hover:shadow-lg bg-dark-gray"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-gray flex items-center justify-center">
                    {React.cloneElement(metric.icon, {
                      className: "w-6 h-6 text-light-gray",
                    })}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-mid-gray uppercase font-medium tracking-wide">
                      {metric.title}
                    </span>
                    <span className="text-2xl font-bold text-light-gray mt-1">
                      {metric.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Tab>

        <Tab eventKey="bookings" title="Bookings Chart">
          <div className="bg-dark-gray p-4 rounded-xl shadow-custom border border-primary mt-4">
            <Bar
              data={bookingsData}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
              }}
            />
          </div>
        </Tab>

        <Tab eventKey="revenue" title="Revenue Chart">
          <div className="bg-dark-gray p-4 rounded-xl shadow-custom border border-primary mt-4">
            <div className="flex justify-center gap-2 mb-4">
              {["day", "week", "month", "year"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1 rounded-md text-sm font-medium border ${
                    range === r
                      ? "bg-sky-600 text-white border-sky-400"
                      : "bg-dark-gray text-mid-gray border-mid-gray"
                  } transition`}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>

            <Line
              data={getRevenueData()}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                    labels: {
                      color: "#e5e7eb",
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `$${context.raw.toLocaleString()}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    ticks: { color: "#e5e7eb" },
                    grid: { color: "#374151" },
                  },
                  y: {
                    ticks: {
                      callback: (value) => `$${value}`,
                      color: "#e5e7eb",
                    },
                    grid: { color: "#374151" },
                  },
                },
              }}
            />
          </div>
        </Tab>

        <Tab eventKey="recent" title="Recent Bookings">
          <div className="overflow-x-auto bg-dark-gray rounded-xl shadow-custom border border-primary mt-4">
            <table className="table-auto w-full text-left border-collapse">
              <thead className="bg-mid-gray text-light-gray">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Client</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {metrics.latestBookings.map((b) => (
                  <tr key={b.id} className="hover:bg-mid-gray transition">
                    <td className="px-4 py-2">{b.id}</td>
                    <td className="px-4 py-2">{b.nombre_cliente}</td>
                    <td className="px-4 py-2">{b.fecha}</td>
                    <td className="px-4 py-2">{b.hora_inicio}</td>
                    <td className="px-4 py-2 capitalize">{b.estado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tab>

        <Tab eventKey="calendar" title="Calendar">
          <CalendarBookings bookings={metrics.calendarBookings} />
        </Tab>
      </Tabs>
    </div>
  );
};

export default DashboardHome;
